// =====================================================================
// Cold-Domain Management Modes (Task #391)
// =====================================================================
// A workspace's cold-sending infrastructure (domains + mailboxes) is run
// under one of three commercial/management modes:
//
//   • lease — Clarik provisions + manages the domains/mailboxes on the
//     tenant's behalf. All managed deliverability features are ON. The
//     tenant rents the infrastructure; it is NOT portable on exit.
//   • buy   — Same managed features as lease, but the tenant OWNS the
//     domains/mailboxes. Priced higher than lease, and (unlike lease) the
//     infrastructure is portable — the tenant takes it with them on exit —
//     and they get custom-naming control.
//   • byod  — "Bring Your Own Domain/mailbox". The tenant connects their
//     own sending infrastructure. Clarik does NOT run managed features
//     (pause-on-reply, warmup, IP rotation, auto-Postmaster, managed reply
//     forwarding) because it does not own/operate that infrastructure.
//
// This module is the SINGLE SOURCE OF TRUTH for which features each mode
// gets and for lease-vs-buy pricing derivation, so the server gating and
// the (separate Level-2) UI never drift from one another. It lives in
// `shared/` precisely so the client can import the same definitions.
//
// NOTE: This task only RECORDS the mode + gates features off it. It does
// NOT implement domain-transfer execution (buy "portability" is framing
// copy only here) and it does NOT implement Postmaster auto-enrollment
// plumbing (`autoPostmaster` only records eligibility — the enrollment is
// a separate Level-1 task).

export type ColdManagementMode = "lease" | "buy" | "byod";

export const COLD_MANAGEMENT_MODES: ColdManagementMode[] = ["lease", "buy", "byod"];

export const DEFAULT_COLD_MANAGEMENT_MODE: ColdManagementMode = "lease";

/** Narrow an untrusted string to a valid mode, falling back to the default. */
export function normalizeColdManagementMode(
  value: unknown,
): ColdManagementMode {
  return value === "lease" || value === "buy" || value === "byod"
    ? value
    : DEFAULT_COLD_MANAGEMENT_MODE;
}

/** Lease + buy are "managed" (Clarik operates the infra). BYOD is not. */
export function isManagedMode(mode: ColdManagementMode): boolean {
  return mode === "lease" || mode === "buy";
}

/**
 * The feature matrix for a mode. Every server-side gate keys off this so
 * adding/removing a managed feature is a one-line change here.
 */
export interface ManagedFeatureMatrix {
  /** Inbound cold reply pauses the contact's active drip enrollment(s). */
  pauseOnReply: boolean;
  /** Clarik auto-manages warmup ramp (sets warmup_start_date, tiered caps). */
  warmup: boolean;
  /** Tenant may request provider-side replacement (IP/mailbox rotation). */
  ipRotation: boolean;
  /** Eligible for managed Google Postmaster auto-enrollment (record only). */
  autoPostmaster: boolean;
  /** Inbound cold reply is auto-forwarded to a configured human mailbox. */
  managedReplyForwarding: boolean;
  /** Infrastructure travels with the tenant on exit (buy only). */
  portableOnExit: boolean;
  /** Tenant controls custom domain/mailbox naming (buy only). */
  customNaming: boolean;
}

export function managedFeaturesForMode(
  mode: ColdManagementMode,
): ManagedFeatureMatrix {
  const managed = isManagedMode(mode);
  return {
    pauseOnReply: managed,
    warmup: managed,
    ipRotation: managed,
    autoPostmaster: managed,
    managedReplyForwarding: managed,
    portableOnExit: mode === "buy",
    customNaming: mode === "buy",
  };
}

// ── Pricing derivation ────────────────────────────────────────────────
// Buy is priced as a consistent multiple of the lease price so the two
// figures can never drift. The lease price itself is derived upstream from
// the authoritative package + markup model (and ultimately the send-cap
// model), so multiplying it here keeps buy pinned to the same source.
export const BUY_PRICE_MULTIPLIER = 1.6;

/** Derive the buy-mode cents from the lease-mode cents (rounds up). */
export function deriveBuyCents(leaseCents: number): number {
  return Math.ceil(leaseCents * BUY_PRICE_MULTIPLIER);
}

// ── Decision-point framing copy ───────────────────────────────────────
// Sales framing surfaced at the lease/buy/byod decision point. Kept here
// (not hardcoded in a component) so the copy is consistent everywhere and
// the Level-2 UI task can import it verbatim.
export interface ColdModeFraming {
  mode: ColdManagementMode;
  label: string;
  tagline: string;
  description: string;
  /** Short bullet points for the decision card. */
  highlights: string[];
}

export const COLD_MODE_FRAMING: Record<ColdManagementMode, ColdModeFraming> = {
  lease: {
    mode: "lease",
    label: "Lease",
    tagline: "Fully managed, lowest cost to start.",
    description:
      "We provision and run the cold-sending domains and mailboxes for you — " +
      "warmup, reply handling, IP rotation, and deliverability monitoring are " +
      "all handled. You rent the infrastructure month to month. It is not " +
      "portable: if you leave, the leased domains and mailboxes stay with us.",
    highlights: [
      "Lowest upfront cost",
      "Fully managed warmup + reply handling",
      "Automatic IP rotation",
      "Postmaster-eligible",
      "Not portable on exit",
    ],
  },
  buy: {
    mode: "buy",
    label: "Buy",
    tagline: "You own it — take it with you on exit.",
    description:
      "Same fully-managed warmup, reply handling, and IP rotation as Lease, but " +
      "you own the domains and mailboxes outright. You get custom naming, and if " +
      "you ever leave, you take the infrastructure with you. Priced higher than " +
      "Lease to reflect ownership.",
    highlights: [
      "You own the domains + mailboxes",
      "Take it with you on exit (portable)",
      "Custom naming control",
      "Fully managed warmup + reply handling",
      "Postmaster-eligible",
      "Higher price than Lease",
    ],
  },
  byod: {
    mode: "byod",
    label: "Bring Your Own",
    tagline: "Connect your own infrastructure — you manage deliverability.",
    description:
      "Connect domains and mailboxes you already own and operate. Because we do " +
      "not run this infrastructure, the managed deliverability features are OFF: " +
      "no auto warmup, no automatic reply pausing, no IP rotation, and no " +
      "Postmaster auto-enrollment. You are responsible for warmup, reputation, " +
      "and compliance on your own infrastructure.",
    highlights: [
      "Use infrastructure you already own",
      "No managed warmup",
      "No automatic reply pausing or forwarding",
      "No IP rotation",
      "Not Postmaster-eligible",
      "You manage deliverability + compliance",
    ],
  },
};
